import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { UnRx } from '@pebula/utils';
import { downloadFile, getLastBusinessDay, removeBusinessDays } from '@sg/utils/src';
import { CitiService, SwiftForwardingApiClient } from '@sg/api/citi-service';
import { InspectorModel } from './inspector-form-model';

export const STEPPER_INDEX_MAP = {
  selectPartner: 0,
  shortcutSearch: 1,
  expandedSearch: 2,
  outboundFromShortcutSearch: 3,
  outboundFromExpandedSearch: 4,
  inbound: 5,
};

const DATE_RANGE_MAP = {
  0: 'last 3 business days',
  1: 'last 24 hours',
  2: 'last 12 hours',
  3: 'last 6 hours',
  4: 'last hour',
};

@Component({
  selector: 'sg-inspector-wizard-dialog',
  templateUrl: './inspector-wizard-dialog.component.html',
  styleUrls: ['./inspector-wizard-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('changeDialogSize', [
      state('selectPartner', style({ width: '600px', height: '513px' })),
      state('shortcutSearch', style({ width: '600px', height: '332px' })),
      state('expandedSearch', style({ width: '600px', height: '435px' })),
      state('expandedSearchNoResults', style({ width: '600px', minHeight: '382px', maxHeight: '422px' })),
      state('table', style({ width: '700px', minHeight: '592px', maxHeight: '650px' })),
      transition('* <=> *', animate('300ms ease-in-out')),
    ]),
  ],
  standalone: false,
})
@UnRx()
export class SgInspectorWizardDialogComponent implements OnInit {
  @ViewChild('inspectorStepper', { static: true }) matStepper: MatStepper;
  inspectorDialogSizeState = 'selectPartner';
  inspectorFormModel = new InspectorModel();
  noResults$ = new BehaviorSubject<boolean>(false);
  outboundSwiftFiles$ = new BehaviorSubject<Array<CitiService.OutboundSwiftDto>>(null);
  outboundSwiftFileContent$ = new BehaviorSubject<string>(null);
  inboundSwiftFiles$ = new BehaviorSubject<Array<CitiService.InboundFileDto>>(null);
  inboundSwiftFileContent$ = new BehaviorSubject<string>(null);
  expandedSearchCriteria: {
    keys: Array<string>;
    values: Array<string | number>;
  };
  fileNameSearchCriteria: string;
  parsedBreadcrumbs: string;
  systemStatusImagesPath: string;
  inspectorImage: string;
  selectedFile: {
    fileContent: string;
    fileName: string;
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { assetsPath: string },
    public dialog: MatDialog,
    public swiftForwardingApi: SwiftForwardingApiClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.getImages();
  }

  getDateRange(dateRange: number): { startDate: string; endDate: string } {
    const dateRanges: { startDate: DateTime; endDate: DateTime } = {
      startDate: null,
      endDate: DateTime.utc(),
    };

    switch (dateRange) {
      case 0:
        dateRanges.startDate = removeBusinessDays(3, DateTime.utc());
        break;
      case 1:
        dateRanges.startDate = DateTime.utc().minus({ hours: 24 });
        break;
      case 2:
        dateRanges.startDate = DateTime.utc().minus({ hours: 12 });
        break;
      case 3:
        dateRanges.startDate = DateTime.utc().minus({ hours: 6 });
        break;
      case 4:
        dateRanges.startDate = DateTime.utc().minus({ hours: 1 });
        break;
      default:
        break;
    }

    return {
      startDate: dateRanges.startDate?.startOf('day').toISO(),
      endDate: dateRanges.endDate?.endOf('day').toISO(),
    };
  }

  navigateFromOutbound(selectedStep: keyof typeof STEPPER_INDEX_MAP): void {
    if (selectedStep === 'inbound') {
      this.getInboundFiles();
    }
    this.goToStep(selectedStep);
  }

  resetSearch(): void {
    this.noResults$.next(false);
    this.goToStep('selectPartner');
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  goToStep(selectedStep: keyof typeof STEPPER_INDEX_MAP): void {
    this.setDialogSizeState(selectedStep);
    this.matStepper.selectedIndex = STEPPER_INDEX_MAP[selectedStep];
  }

  startInvestigation(selectedPartner: string): void {
    this.parseBreadcrumbs(selectedPartner);
    this.goToStep('shortcutSearch');
  }

  getOutboundFilesByFileName(): void {
    const fileName = this.inspectorFormModel.fileName;
    const body = {
      recipient: this.inspectorFormModel.partnerOrClient,
      fileName,
    };
    this.swiftForwardingApi
      .swiftForwardingControllerPostSwiftSearchOutbound({ body }, { observe: 'body' })
      .pipe(UnRx(this))
      .subscribe((response) => {
        if (response?.rows?.length > 0) {
          this.outboundSwiftFiles$.next(response.rows);
          this.fileNameSearchCriteria = fileName;
          this.noResults$.next(false);
          this.goToStep('outboundFromShortcutSearch');
        } else {
          this.noResults$.next(true);
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        }
      });
  }

  getOutboundFilesByExpandedSearch(): void {
    const dateRange = this.getDateRange(this.inspectorFormModel.dateRange);
    const body = {
      recipient: this.inspectorFormModel.partnerOrClient,
      isin: this.checkIfEmpty(this.inspectorFormModel.isin),
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      units: this.inspectorFormModel.units ? Number(this.inspectorFormModel.units) : null,
      bic: this.checkIfEmpty(this.inspectorFormModel.bic),
    };

    this.swiftForwardingApi
      .swiftForwardingControllerPostSwiftSearchOutbound({ body }, { observe: 'body' })
      .pipe(UnRx(this))
      .subscribe((response) => {
        if (response?.rows?.length > 0) {
          this.outboundSwiftFiles$.next(response.rows);
          this.resetExpandedSearchCriteria();
        } else {
          this.getExpandedSearchCriteria();
        }
        this.goToStep('outboundFromExpandedSearch');
      });
  }

  getOutboundFileContentById(id: string): void {
    const file$: Observable<HttpResponse<Blob>> = this.swiftForwardingApi
      .swiftForwardingControllerGetSwiftOutboundFileByid({ id }, { observe: 'response', responseType: 'blob' })
      .pipe(shareReplay(1));

    this.extractFile(file$)
      .pipe(UnRx(this))
      .subscribe((file) => {
        this.selectedFile = file;
        this.outboundSwiftFileContent$.next(file.fileContent);
      });
  }

  getInboundFiles(): void {
    const body: CitiService.SearchInboundSwiftRequest = {
      statuses: ['New', 'Processing', 'Error'],
      startDate: getLastBusinessDay().toISO(),
      endDate: DateTime.utc().toISO(),
    };

    this.swiftForwardingApi
      .swiftForwardingControllerPostSwiftSearchInbound({ body }, { observe: 'body' })
      .pipe(UnRx(this))
      .subscribe((response) => {
        this.inboundSwiftFiles$.next(response.rows);
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
  }

  getInboundFileContentById(id: string): void {
    const file$: Observable<HttpResponse<Blob>> = this.swiftForwardingApi
      .swiftForwardingControllerGetSwiftInboundFileByid({ id }, { observe: 'response', responseType: 'blob' })
      .pipe(shareReplay(1));

    this.extractFile(file$)
      .pipe(UnRx(this))
      .subscribe((file) => {
        this.selectedFile = file;
        this.inboundSwiftFileContent$.next(file.fileContent);
      });
  }

  downloadSelectedFile(): void {
    downloadFile(this.selectedFile.fileContent, '', this.selectedFile.fileName);
  }

  private extractFile(file$: Observable<HttpResponse<Blob>>): Observable<{ fileContent: string; fileName: string }> {
    return file$.pipe(
      switchMap((file) => file.body.text()),
      withLatestFrom(file$),
      map(([fileContent, file]) => {
        const fileName = this.getFileNameFromHeaders(file);
        return { fileContent, fileName };
      })
    );
  }

  private parseBreadcrumbs(selectedPartner: string): void {
    this.parsedBreadcrumbs = `> ${this.inspectorFormModel.fileType} / ${selectedPartner}`;
  }

  private getImages(): void {
    this.systemStatusImagesPath = this.data.assetsPath + 'images/system-status/';
    this.inspectorImage = this.systemStatusImagesPath + 'inspector-icon.svg';
  }

  private setDialogSizeState(selectedStep: keyof typeof STEPPER_INDEX_MAP): void {
    switch (selectedStep) {
      case 'selectPartner':
        this.inspectorDialogSizeState = 'selectPartner';
        break;
      case 'shortcutSearch':
        this.inspectorDialogSizeState = 'shortcutSearch';
        break;
      case 'expandedSearch':
        this.inspectorDialogSizeState = 'expandedSearch';
        break;
      case 'outboundFromExpandedSearch':
        if (this.expandedSearchCriteria?.values) {
          this.inspectorDialogSizeState = 'expandedSearchNoResults';
        } else {
          this.inspectorDialogSizeState = 'table';
        }
        break;
      case 'outboundFromShortcutSearch':
      case 'inbound':
        this.inspectorDialogSizeState = 'table';
        break;
      default:
        break;
    }
  }

  private checkIfEmpty(value: string): string {
    if (value?.length === 0) {
      return null;
    } else {
      return value;
    }
  }

  private getFileNameFromHeaders(file: HttpResponse<Blob>): string {
    const findFileNameRegEx = /filename=([^;]+)/gi;
    const contentDisposition = file.headers.get('Content-Disposition') || '';
    const matches = findFileNameRegEx.exec(contentDisposition);
    const fileName = (matches[1] || 'untitled').trim();
    return fileName;
  }

  private getExpandedSearchCriteria(): void {
    const searchCriteria = {
      ISIN: this.inspectorFormModel.isin,
      range: DATE_RANGE_MAP[this.inspectorFormModel.dateRange],
      units: this.inspectorFormModel.units,
      BIC: this.inspectorFormModel.bic,
    };
    const filteredCriteria = Object.fromEntries(Object.entries(searchCriteria).filter(([key, value]) => value));

    this.expandedSearchCriteria = {
      keys: Object.keys(filteredCriteria),
      values: Object.values(filteredCriteria),
    };
  }

  private resetExpandedSearchCriteria(): void {
    this.expandedSearchCriteria = {
      keys: null,
      values: null,
    };
  }
}
